/**
 * LogoutConfirmation — shown when db.cloud.logout() detects unsynced changes.
 *
 * ⚠️ CRITICAL — DATA SAFETY
 *
 * This fires as DXCUserInteraction type: "logout-confirmation".
 * The alerts array contains LOGOUT_CONFIRMATION with numUnsyncedChanges.
 *
 * NEVER-17: Cancel MUST be the default/prominent action.
 * NEVER-01: Submit means the user explicitly accepts data loss.
 *
 * Cancel → keeps data, stays logged in
 * Submit → proceeds with logout, unsynced changes are lost
 */

import { type DXCUserInteraction, resolveText } from "dexie-cloud-addon"
import { useTranslation } from "react-i18next"
import { Button } from "@/app/components/ui/button"
import { CircularProgress } from "@/app/components/ui/CircularProgress"

interface LogoutConfirmationProps {
  alerts: DXCUserInteraction["alerts"]
  onSubmit: DXCUserInteraction["onSubmit"]
  onCancel?: DXCUserInteraction["onCancel"]
  submitLabel?: string
  cancelLabel?: string | null
}

export function LogoutConfirmation({
  alerts,
  onSubmit,
  onCancel,
  submitLabel,
  cancelLabel,
}: LogoutConfirmationProps) {
  const { t } = useTranslation()

  // Extract unsynced change count from alert params
  const logoutAlert = alerts.find(
    (a) => a.messageCode === "LOGOUT_CONFIRMATION",
  )
  const numUnsynced = Number(
    logoutAlert?.messageParams?.numUnsyncedChanges ?? 0,
  )

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-8 shadow-lg">
        {/* Warning icon */}
        <div className="mb-4 flex justify-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--yellow-500)]/10">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--yellow-500)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>
        </div>

        {/* Title */}
        <h2 className="mb-2 text-center text-lg font-semibold">
          {t("Unsynced changes")}
        </h2>

        {/* Message */}
        <p className="mb-2 text-center text-sm text-muted-foreground">
          {numUnsynced > 0
            ? t(
                "You have {{count}} unsynced changes that will be lost if you sign out.",
                {
                  count: numUnsynced,
                },
              )
            : t("You have unsynced changes that will be lost if you sign out.")}
        </p>

        {/* Dexie Cloud alerts */}
        {alerts.map((alert, i) => (
          <div
            key={i}
            className="mb-4 rounded-lg bg-[var(--yellow-500)]/10 px-4 py-3 text-center text-sm text-[var(--yellow-500)]"
          >
            {resolveText(alert)}
          </div>
        ))}

        {/* Actions — Cancel is DEFAULT (prominent), Submit is destructive */}
        <div className="flex flex-col gap-2">
          {/* Cancel = SAFE = DEFAULT = prominent */}
          {onCancel ? (
            <Button
              type="button"
              variant="primary"
              onClick={onCancel}
              className="h-12 w-full text-base"
              autoFocus
            >
              {cancelLabel ?? t("Keep my data")}
            </Button>
          ) : (
            /* onCancel/onSubmit undefined = Dexie Cloud still loading callback */
            <div className="flex justify-center py-3">
              <CircularProgress className="size-6" />
            </div>
          )}

          {/* Submit = DESTRUCTIVE = secondary, non-prominent */}
          {onSubmit && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => onSubmit({})}
              className="h-10 w-full text-sm text-[var(--red-500)] hover:text-[var(--red-700)]"
            >
              {submitLabel ?? t("Sign out anyway")}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
